import { typeValidator } from '@chilipiper/utils'
import * as R from 'remeda'
import React, {
  Children,
  ComponentPropsWithoutRef,
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Collection, Node } from 'react-stately'
import styled from 'styled-components'
import { Skeleton, SkeletonLine } from '../../../../old'
import { Box, Text } from '../../../core-components'
import { StartContentProvider } from '../../data'
import { CascadeSelectItem, SelectItem, SelectSection } from '../select/PublicComponents'
import { DynamicCascadeListContext } from './CascadeItemSelectList'
import { SelectedText } from './StyledSelectTrigger'
import { DynamicItem } from './dynamicItemUtils'
import { CascadeSelectItemPublicComponent, SelectItemPublicComponent } from './publicComponentTypes'

// this is named and looks like component just to trick eslint, it's used as regular funciton in BaseSelectValue
// the trick is to avoid errors on hook usage here, they somehow work (though this function is being called conditionally and they may stop working)
export const DefaultSelectedText = (
  selectedItem: Node<DynamicItem>,
  valuePath: readonly string[] | undefined,
  value?: string,
  onLeafEndContent?: (endContent: ReactNode | undefined) => void
) => {
  const { label, hiddenListsToRender } = useLabelPath(
    selectedItem,
    [...(valuePath ?? []), value].filter((v): v is string => !!v)
  )

  const lastItem = label.at(-1)
  useEffect(() => {
    onLeafEndContent?.(lastItem?.endContent)
  }, [lastItem?.endContent, onLeafEndContent])
  return (
    <Box>
      {label.length > 1 && (
        <FlexText
          as='div'
          mb='0.4rem'
          color='fg/moderate'
          textStyle='heading-2xs'
          whiteSpace='nowrap'
          textOverflow='ellipsis'
          overflow='hidden'
        >
          {label.slice(0, -1).map((item, index) => {
            if (index === 0) {
              return (
                <Fragment key={index}>
                  {item.startContent ? (
                    <Box mr={2}>
                      <StartContentProvider value={{}}>{item.startContent}</StartContentProvider>
                    </Box>
                  ) : (
                    ''
                  )}
                  {item.label}
                  {item.endContent ? <>&nbsp;{item.endContent}</> : ''}
                </Fragment>
              )
            }
            return (
              <FlexText
                key={index}
                color='fg/moderate'
                textStyle='heading-2xs'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
                overflow='hidden'
              >
                &nbsp;&gt;&nbsp;
                {item.startContent ? (
                  <Box mr={2}>
                    <StartContentProvider value={{}}>{item.startContent}</StartContentProvider>
                  </Box>
                ) : (
                  ''
                )}
                {item.label}
                {item.endContent ? <>&nbsp;{item.endContent}</> : ''}
              </FlexText>
            )
          })}
        </FlexText>
      )}
      {typeof lastItem?.label === 'string' ? (
        <FlexText>
          {lastItem.startContent ? (
            <Box display='inline-block' mr={2}>
              <StartContentProvider value={{}}>{lastItem.startContent}</StartContentProvider>
            </Box>
          ) : (
            ''
          )}
          <SelectedText
            as='div'
            color='fg/major'
            textStyle='form-value'
            whiteSpace='nowrap'
            textOverflow='ellipsis'
            overflow='hidden'
            lineHeight={
              8 /* Overflow hidden clips the text otherwise, use negative margin to offset it */
            }
            my={-1}
          >
            {lastItem.label}
          </SelectedText>
        </FlexText>
      ) : (
        <SelectedText as='div' color='fg/major' textStyle='form-value'>
          {lastItem?.label}
        </SelectedText>
      )}
      <div aria-hidden style={{ display: 'none' }}>
        {hiddenListsToRender}
      </div>
    </Box>
  )
}

export const useLabelPath = (rootNode: Node<DynamicItem>, valuePath: readonly string[]) => {
  const { getItemChildren, getDynamicCascadeListProviderData } = useLazyCascadeLists()

  if (!rootNode.value) {
    return {
      label: [
        {
          label: rootNode.rendered,
          endContent: undefined,
          startContent: undefined,
          isDisabled: false,
        },
      ],
      hiddenListsToRender: [],
    }
  }
  const firstSegment = {
    value: dynamicItemValue(rootNode.value),
    labelElement: rootNode.value?.itemType === 'cascade' ? rootNode.value.label : rootNode.rendered,
    loaded: true,
    endContent: dynamicItemEndContent(rootNode.value),
    startContent: dynamicItemStartContent(rootNode.value),
    children:
      rootNode.value.itemType === 'cascade' ? Children.toArray(rootNode.value.children) : [],
    isDisabled: false,
  }
  const getLabelPathFromStaticChildren = (path: readonly string[], value: string) => {
    const loadedItemChildren = getItemChildren(path)
    if (loadedItemChildren) {
      const item = findItemInDynamicItemChildren(loadedItemChildren, value)
      return {
        value,
        labelElement:
          item?.itemType === 'cascade'
            ? item.label
            : (item?.children ?? <LabelSkeleton key={value} />),
        endContent: item?.endContent,
        startContent: item?.startContent,
        isDisabled: Boolean(item?.isDisabled),
        loaded: true,
        children: Children.toArray(item?.children ?? []),
      }
    }
  }
  const getLabelPathFromDynamicChildren = (
    prevNodeChildren: readonly ReactNode[],
    value: string
  ) => {
    const nextItemNode = findItemInReactChildren(prevNodeChildren, value)
    if (nextItemNode) {
      return {
        value,
        labelElement: typeValidator(CascadeSelectItem)(nextItemNode)
          ? nextItemNode.props.label
          : (nextItemNode.props.children ?? <LabelSkeleton key={value} />),
        loaded: true,
        endContent: nextItemNode.props.endContent || undefined,
        startContent: nextItemNode.props.startContent,
        children: typeValidator(CascadeSelectItem)(nextItemNode)
          ? Children.toArray(nextItemNode.props.children)
          : [],
        isDisabled: Boolean(nextItemNode.props.isDisabled),
      }
    }
  }
  const hiddenListsToRender: ReactElement[] = []
  const path = valuePath.slice(1).reduce(
    (labelPath, value) => {
      const prevNodeChildren = labelPath[labelPath.length - 1].children
      const path = labelPath.map(item => item.value)
      const staticLabelPath = getLabelPathFromStaticChildren(path, value)
      if (staticLabelPath) {
        labelPath.push(staticLabelPath)
        return labelPath
      }
      const dynamicLabelPath = getLabelPathFromDynamicChildren(prevNodeChildren, value)
      if (dynamicLabelPath) {
        labelPath.push(dynamicLabelPath)
        return labelPath
      }

      if (prevNodeChildren.length === 1) {
        const [onlyChild] = prevNodeChildren
        if (!typeValidator(SelectSection, CascadeSelectItem, SelectItem)(onlyChild)) {
          hiddenListsToRender.push(
            <DynamicCascadeListContext.Provider
              value={getDynamicCascadeListProviderData(path)}
              key={path.join('.')}
            >
              {onlyChild}
            </DynamicCascadeListContext.Provider>
          )
        }
      }
      labelPath.push({
        value,
        labelElement: <LabelSkeleton key={value} />,
        loaded: false,
        endContent: undefined,
        startContent: undefined,
        children: [],
        isDisabled: false,
      })

      return labelPath
    },
    [firstSegment]
  )
  return {
    label: path.map(item => ({
      label: item.labelElement,
      endContent: item.endContent,
      startContent: item.startContent,
      isDisabled: item.isDisabled,
    })),
    hiddenListsToRender,
  }
}

/**
 * We are rendering currently selected path of items in a hidden div, this is to force loading of
 * selected items in the cascade select.
 *
 * In the example below, on mounting such select we need to mount
 * SomeComponentThatWillLoadItemBOnMount to force loading of item b. After render of
 * SomeComponentThatWillLoadItemBOnMount we're setting the value of label of this item into state of
 * select component, which causes re-render (this is required) and in turn it rerenders
 * SomeComponentThatWillLoadItemBOnMount most likely causing it to create new children (unless it
 * memoized). This will cause us to receive new (usually identical, but still new) item B and that
 * cuses another re-render. This way we're ending up with infinite loop of re-renders.
 *
 * To fix that, we're not relying ont react dependency array to check when to set the state, but
 * instead we're comparing the values of items rendered by dynamic components like
 * SomeComponentThatWillLoadItemBOnMount. This function is used to compare what we currently have in
 * state with what we're about to put there, to not update state when values are the same and not
 * triger re-render loop
 *
 * @example
 *   ;<Select valuePtah={['a']} value='b'>
 *     <Select.CascadeItem value='a'>
 *       <SomeComponentThatWillLoadItemBOnMount />
 *     </Select.CascadeItem>
 *   </Select>
 */
const areDynamicItemsEqual = (a: readonly DynamicItem[], b: readonly DynamicItem[]) => {
  const compareStack = a.map<{ itemA: ItemCompareData; itemB: ItemCompareData }>(
    (itemA, index) => ({ itemA, itemB: b[index] })
  )
  while (compareStack.length) {
    const { itemA, itemB } = compareStack.pop() ?? {}
    if ((!itemA && itemB) || (itemA && !itemB)) {
      return false
    }
    if (!itemA || !itemB) {
      continue
    }
    const { result, nextItems } = compareSingleItems(itemA, itemB)
    if (!result) {
      return false
    }
    compareStack.push(...nextItems)
  }
  return true
}

const compareSingleItems = (itemA: ItemCompareData, itemB: ItemCompareData) => {
  switch (itemA.itemType) {
    case 'item':
      return { result: itemB.itemType === 'item' && itemA.value === itemB.value, nextItems: [] }
    case 'section':
      return itemB.itemType === 'section' && itemA.title === itemB.title
        ? {
            result: true,
            nextItems: itemA.children.map((itemA, index) => ({
              itemA,
              itemB: itemB.children[index],
            })),
          }
        : { result: false, nextItems: [] }
    case 'cascade':
      if (itemB.itemType !== 'cascade' || itemA.value !== itemB.value) {
        return { result: false, nextItems: [] }
      }
      return {
        nextItems: mapReactChildrenToCompareItemsArray(itemA.children, itemB.children),
      }
  }
  return { nextItems: [] }
}

const mapReactChildrenToCompareItemsArray = (childrenA: ReactNode, childrenB: ReactNode) => {
  const itemAChildren = Children.toArray(childrenA).map(reactNodeToItemCompareData)
  const itemBChildren = Children.toArray(childrenB).map(reactNodeToItemCompareData)
  return itemAChildren.map((itemA, index) => ({ itemA, itemB: itemBChildren[index] }))
}

type ItemCompareData =
  | {
      readonly itemType: 'item'
      readonly value: string
    }
  | {
      readonly children: readonly ItemCompareData[]
      readonly itemType: 'section'
      readonly title: string
    }
  | {
      readonly children: ReactNode
      readonly itemType: 'cascade'
      readonly value: string
    }
  | {
      readonly itemType: 'unknown'
    }

const reactNodeToItemCompareData = (node: ReactNode): ItemCompareData => {
  if (typeValidator(SelectItem)(node)) {
    return { itemType: 'item', value: node.props.value }
  }
  if (typeValidator(SelectSection)(node)) {
    return {
      itemType: 'section',
      title: node.props.title,
      children: Children.toArray(node.props.children).map(reactNodeToItemCompareData),
    }
  }
  if (typeValidator(CascadeSelectItem)(node)) {
    return {
      itemType: 'cascade',
      value: node.props.value,
      children: node.props.children,
    }
  }
  // this is some unknown dynamic component, we may want to compare add all it's props for comparison, but this is most likely not needed...
  return {
    itemType: 'unknown',
  }
}

const findItemInDynamicItemChildren = (
  dynamicItemChildren: readonly DynamicItem[],
  value: string
) =>
  dynamicItemChildren
    .flatMap(item => {
      if (item.itemType === 'section') {
        return item.children
      }
      return [item]
    })
    .find(item => item.value === value)

const findItemInReactChildren = (children: readonly ReactNode[], value: string) =>
  children
    .flatMap(item => {
      if (typeValidator(SelectSection)(item)) {
        return Children.toArray(item.props.children)
      }
      return [item]
    })
    .find((child): child is SelectItemPublicComponent | CascadeSelectItemPublicComponent => {
      if (typeValidator(SelectItem)(child) && child.props.value === value) {
        return true
      }
      if (typeValidator(CascadeSelectItem)(child) && child.props.value === value) {
        return true
      }
      return false
    })

const dynamicItemValue = (item: DynamicItem) => {
  if (item.itemType === 'cascade') {
    return item.value
  }
  if (item.itemType === 'section') {
    return item.title
  }
  return item.value
}

const dynamicItemEndContent = (item: DynamicItem) => {
  if (item.itemType === 'cascade') {
    return item.endContent
  }
  if (item.itemType === 'section') {
    return undefined
  }
  return item.endContent
}

const dynamicItemStartContent = (item: DynamicItem) => {
  if (item.itemType === 'cascade') {
    return item.startContent
  }
  if (item.itemType === 'section') {
    return undefined
  }
  return item.startContent
}

const useLazyCascadeLists = () => {
  const [itemChildren, setItems] = useState<{ [path: string]: DynamicItem[] }>({})
  const dynamicCascadeListProviderData = useRef<{
    [path: string]: ComponentPropsWithoutRef<typeof DynamicCascadeListContext.Provider>['value']
  }>({})
  const eventsRef = useCallback(
    (
      data: {
        collection: Collection<Node<DynamicItem>>
      },
      path: string
    ) => {
      setItems(oldItems => {
        const newNodes = [...data.collection]
          .map(node => node.value)
          .filter((v): v is Exclude<typeof v, undefined | null> => !!v)
        if (!oldItems[path]) {
          return { ...oldItems, [path]: newNodes }
        }
        const prevNodes = oldItems[path]
        if (areDynamicItemsEqual(prevNodes, newNodes)) {
          return oldItems
        }

        return { ...oldItems, [path]: newNodes }
      })
      return () => {
        dynamicCascadeListProviderData.current[path] = undefined
      }
    },
    []
  )
  const getDynamicCascadeListProviderData = (path: readonly string[]) => {
    const pathString = path.join('/')
    if (dynamicCascadeListProviderData.current[pathString]) {
      return dynamicCascadeListProviderData.current[pathString]
    }
    const newData = {
      onChange: R.doNothing(),
      onClose: R.doNothing(),
      onNextList: R.doNothing(),
      onPrevList: R.doNothing(),
      onSelectCascadeItem: R.doNothing(),
      onFocusItem: R.doNothing(),
      path: pathString,
      eventsRef,
      value: '',
    }
    dynamicCascadeListProviderData.current[pathString] = newData
    return newData
  }
  return {
    getDynamicCascadeListProviderData,
    getItemChildren: (path: readonly string[]) => itemChildren[path.join('/')],
  }
}

const LabelSkeleton = () => {
  return (
    <Skeleton width={100} height={14}>
      <SkeletonLine width={100} />
    </Skeleton>
  )
}

const FlexText = styled(Text)`
  display: flex;
  align-items: center;
  > div {
    flex-grow: 0;
    flex-shrink: 0;
  }
`
